import React from 'react';
import { type PresentacionRequest, type PresentacionFormErrors } from '../types/especie';
import { EspecieValidation } from '../services/especieValidation';
import { EspecieService } from '../services/especieService';

interface UsePresentacionFormReturn {
  formData: PresentacionRequest;
  errors: PresentacionFormErrors;
  loading: boolean;
  isValid: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSwitchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => Promise<void>;
  resetForm: () => void;
}

export const usePresentacionForm = (
  presentacionId: number | null,
  idEspecie: number,
  onSave: (presentacion: PresentacionRequest) => void
): UsePresentacionFormReturn => {
  const [formData, setFormData] = React.useState<PresentacionRequest>({
    idEspecie,
    descripcion: '',
    esFresco: true,
  });
  const [errors, setErrors] = React.useState<PresentacionFormErrors>({});
  const [loading, setLoading] = React.useState(false);

  const isValid = React.useMemo(() => {
    return EspecieValidation.isValidPresentacion(formData);
  }, [formData]);

  React.useEffect(() => {
    const loadPresentacion = async () => {
      if (presentacionId) {
        setLoading(true);
        try {
          const presentacion = await EspecieService.getPresentacionById(presentacionId);
          setFormData({
            idPresentacion: presentacion.idPresentacion,
            idEspecie: presentacion.idEspecie,
            descripcion: presentacion.descripcion,
            esFresco: presentacion.esFresco,
          });
        } catch (error) {
          console.error('Error loading presentacion:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadPresentacion();
  }, [presentacionId]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear specific error when user starts typing
    if (errors[name as keyof PresentacionFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));

    // Clear specific error when user changes switch
    if (errors[name as keyof PresentacionFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async () => {
    const validationErrors = EspecieValidation.validatePresentacion(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setLoading(true);
      try {
        await onSave(formData);
      } catch (error) {
        console.error('Error saving presentacion:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = React.useCallback(() => {
    setFormData({
      idEspecie,
      descripcion: '',
      esFresco: true,
    });
    setErrors({});
  }, [idEspecie]);

  return {
    formData,
    errors,
    loading,
    isValid,
    handleChange,
    handleSwitchChange,
    handleSubmit,
    resetForm,
  };
};